import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, Filter, ShoppingCart, Tag, CheckCircle, Eye, Music, Video, Image, FileText, Star, ArrowRight, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_FILTERS = [
  { id: 'all', label: 'All Assets' },
  { id: 'audio', label: 'Audio' },
  { id: 'video', label: 'Video' },
  { id: 'image', label: 'Image' },
  { id: 'text', label: 'Text/Copy' },
];

const ICON_MAP = { audio: Music, video: Video, image: Image, text: FileText };

export default function MarketplacePage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState(null);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.asset_type = filter;
      if (searchQuery) params.q = searchQuery;
      const res = await axios.get(`${API}/marketplace/listings`, { params });
      setListings(res.data.listings || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [filter, searchQuery]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const handlePurchase = async (listingId) => {
    if (!user) { navigate('/auth'); return; }
    try {
      const res = await axios.post(`${API}/marketplace/purchase`, {
        listing_id: listingId,
        origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-texture" data-testid="marketplace-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-10 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
            style={{ background: 'rgba(0,240,255,0.08)', border: '1px solid rgba(0,240,255,0.2)' }}>
            <ShoppingCart size={14} className="text-[--ch-cyan]" />
            <span className="font-label text-[--ch-cyan]">IP MARKETPLACE</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black mb-3" data-testid="marketplace-title">
            <span className="text-white">Cleared IP </span>
            <span className="neon-cyan">Marketplace</span>
          </h1>
          <p className="text-base text-[--ch-text-secondary] max-w-lg mx-auto">
            License pre-cleared, .VIG-verified intellectual property for your campaigns. Legal. Instant. Protected.
          </p>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6 animate-fade-in-up stagger-1">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[--ch-text-secondary]" />
            <input
              data-testid="marketplace-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search cleared assets..."
              className="w-full pl-10 pr-4 py-3 rounded-lg bg-[--ch-bg-card] border border-[--ch-border] text-white placeholder-gray-500 focus:border-[--ch-cyan] focus:outline-none transition-colors"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {ASSET_FILTERS.map(f => (
              <button
                key={f.id}
                data-testid={`filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  filter === f.id
                    ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan] border border-[--ch-cyan]'
                    : 'bg-[--ch-bg-card] text-[--ch-text-secondary] border border-[--ch-border] hover:border-gray-500'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-[--ch-cyan] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-[--ch-text-secondary]">Loading marketplace...</p>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16 ch-card p-10" data-testid="marketplace-empty">
            <ShoppingCart size={40} className="text-[--ch-text-secondary] mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-bold mb-2">No listings yet</h3>
            <p className="text-sm text-[--ch-text-secondary] mb-6">
              The marketplace will be populated as creators wrap and list their cleared IP.
            </p>
            <button onClick={() => navigate('/vig')} className="btn-outline-neon px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2">
              Wrap Your IP First <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="marketplace-grid">
            {listings.map((listing) => {
              const TypeIcon = ICON_MAP[listing.asset_type] || FileText;
              return (
                <div key={listing.id} className="ch-card overflow-hidden group cursor-pointer"
                  onClick={() => setSelectedListing(listing)}
                  data-testid={`listing-${listing.id}`}>
                  {/* Preview area */}
                  <div className="h-40 relative overflow-hidden"
                    style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(0,240,255,0.05))' }}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <TypeIcon size={40} className="text-[--ch-purple] opacity-30 group-hover:opacity-50 transition-opacity group-hover:scale-110 transition-transform" />
                    </div>
                    {/* VIG Badge */}
                    <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full"
                      style={{ background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(139,92,246,0.3)' }}>
                      <CheckCircle size={10} className="text-[--ch-success]" />
                      <span className="text-[9px] font-label text-[--ch-success]">.VIG VERIFIED</span>
                    </div>
                    {/* License type */}
                    <div className="absolute top-3 right-3 px-2 py-1 rounded-full"
                      style={{ background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(0,240,255,0.2)' }}>
                      <span className="text-[9px] font-label text-[--ch-cyan]">{listing.license_type || 'PERPETUAL'}</span>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold truncate">{listing.title}</h3>
                        <p className="text-xs text-[--ch-text-secondary] mt-0.5">by {listing.creator_name}</p>
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <Star size={12} className="text-[--ch-warning]" />
                        <span className="text-xs text-[--ch-warning] font-bold">{listing.rating || '4.8'}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs px-2 py-0.5 rounded bg-[rgba(139,92,246,0.1)] text-[--ch-purple] font-bold capitalize">
                        {listing.asset_type}
                      </span>
                      <span className="text-xs text-[--ch-text-secondary]">{listing.downloads || 0} licenses sold</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <p className="text-lg font-black neon-cyan">${listing.price}</p>
                      <button
                        data-testid={`buy-${listing.id}`}
                        onClick={(e) => { e.stopPropagation(); handlePurchase(listing.id); }}
                        className="px-4 py-2 rounded-full text-xs font-bold transition-all"
                        style={{
                          background: 'linear-gradient(135deg, rgba(0,240,255,0.15), rgba(139,92,246,0.15))',
                          border: '1px solid rgba(0,240,255,0.3)',
                          color: '#00F0FF',
                        }}
                      >
                        License
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Detail Modal */}
        {selectedListing && (
          <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
            onClick={() => setSelectedListing(null)}
            style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(12px)' }}
            data-testid="listing-detail-modal">
            <div onClick={(e) => e.stopPropagation()}
              className="ch-card max-w-lg w-full p-6 relative"
              style={{ animation: 'dropIn 0.3s ease-out' }}>
              <button onClick={() => setSelectedListing(null)}
                className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors text-xl">&times;</button>

              <div className="flex items-center gap-2 mb-4">
                <CheckCircle size={16} className="text-[--ch-success]" />
                <span className="font-label text-[--ch-success]">.VIG VERIFIED ASSET</span>
              </div>

              <h2 className="text-xl font-bold mb-1">{selectedListing.title}</h2>
              <p className="text-sm text-[--ch-text-secondary] mb-4">by {selectedListing.creator_name}</p>

              {selectedListing.description && (
                <p className="text-sm text-[--ch-text-secondary] mb-4">{selectedListing.description}</p>
              )}

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="p-3 rounded-lg" style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A' }}>
                  <p className="text-[10px] font-label text-[--ch-text-secondary]">TYPE</p>
                  <p className="text-sm font-bold capitalize">{selectedListing.asset_type}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A' }}>
                  <p className="text-[10px] font-label text-[--ch-text-secondary]">LICENSE</p>
                  <p className="text-sm font-bold">{selectedListing.license_type || 'Perpetual'}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A' }}>
                  <p className="text-[10px] font-label text-[--ch-text-secondary]">SOLD</p>
                  <p className="text-sm font-bold">{selectedListing.downloads || 0} licenses</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A' }}>
                  <p className="text-[10px] font-label text-[--ch-text-secondary]">RATING</p>
                  <p className="text-sm font-bold flex items-center gap-1">
                    <Star size={12} className="text-[--ch-warning]" /> {selectedListing.rating || '4.8'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg mb-4"
                style={{ background: 'rgba(0,240,255,0.03)', border: '1px solid rgba(0,240,255,0.15)' }}>
                <span className="text-2xl font-black neon-cyan">${selectedListing.price}</span>
                <span className="text-xs text-[--ch-text-secondary]">One-time license fee</span>
              </div>

              <button
                data-testid="modal-purchase-btn"
                onClick={() => handlePurchase(selectedListing.id)}
                className="btn-neon w-full py-4 rounded-full text-sm font-semibold flex items-center justify-center gap-2"
              >
                <ShoppingCart size={16} /> Purchase License <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
